/**
 * I18nProvider + useI18n - the interface localization runtime.
 *
 * Mounted inside AuthProvider so it can scope the persisted preference to the signed-in
 * user. Exposes the active locale, a setter that persists, the supported locales, and a
 * `t(key, vars?)` resolver. Strings are looked up by key from the active locale's catalog
 * with a clean fallback to English (see translate.ts).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/auth/AuthContext";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale, type LocaleOption } from "./types";
import { translate } from "./translate";
import { loadLocale, saveLocale } from "./persistence";

interface I18nContextValue {
  locale:  Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  locales: LocaleOption[];
}

const I18nContext = createContext<I18nContextValue>({
  locale:    DEFAULT_LOCALE,
  setLocale: () => {},
  t:         (key, vars) => translate(DEFAULT_LOCALE, key, vars),
  locales:   SUPPORTED_LOCALES,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [locale, setLocaleState] = useState<Locale>(() => loadLocale(null));

  // Re-read the persisted preference whenever the signed-in user changes, so each user
  // gets their own interface language on a shared device (and it survives reload).
  useEffect(() => {
    setLocaleState(loadLocale(userId));
  }, [userId]);

  // Reflect the active locale on <html lang> + dir for accessibility and RTL support.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
      const opt = SUPPORTED_LOCALES.find(l => l.code === locale);
      document.documentElement.dir = opt?.dir ?? "ltr";
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    saveLocale(userId, next);
    setLocaleState(next);
  }, [userId]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, locales: SUPPORTED_LOCALES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
